// RSS Feeds Management Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { appendReturnTo } from '../utils/return-to-url.js';

class RssFeeds extends HTMLElement {
  constructor() {
    super();
    this.feeds = [];
    this.isLoading = false;
    this.showAddForm = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.loadFeeds();
  }

  renderIntro() {
    return `
      <section class="rss-feeds-intro card">
        <h4 class="rss-feeds-intro-title">Your blog, your words, your friends</h4>
        <p class="rss-feeds-intro-body">
          Fun Old Times is built for connection, not captivity. We encourage you to run your own blog
          somewhere you control, then import posts here via RSS. You keep ownership of your writing —
          and your friends still get notified when you publish.
        </p>
        <p class="rss-feeds-intro-body">
          Need a cozy corner for your words? These indie microblogging platforms are great places to start:
        </p>
        <ul class="rss-platform-list">
          <li>
            <a href="https://pckt.blog/" target="_blank" rel="noopener noreferrer">Pckt</a>
            <span class="rss-platform-blurb">— pocket-sized posts, big personality</span>
          </li>
          <li>
            <a href="https://leaflet.pub/home" target="_blank" rel="noopener noreferrer">Leaflet</a>
            <span class="rss-platform-blurb">— simple pages for sharing what&apos;s on your mind</span>
          </li>
          <li>
            <a href="https://offprint.app/" target="_blank" rel="noopener noreferrer">Offprint</a>
            <span class="rss-platform-blurb">— a lovely little home for independent publishing</span>
          </li>
        </ul>
        <p class="rss-feeds-intro-footer">
          Already have a feed? Paste its URL below and we&apos;ll pull in new posts automatically.
          <a href="${appendReturnTo('/docs/rss/import-your-blog')}" class="rss-feeds-help-link">Learn how to import your blog →</a>
        </p>
      </section>
    `;
  }

  render() {
    this.innerHTML = `
      <div class="rss-feeds">
        ${this.renderIntro()}
        <div class="rss-feeds-header">
          <h3>📡 RSS Feeds</h3>
          <button class="btn btn-primary add-feed-btn">+ Add Feed</button>
        </div>

        ${this.showAddForm ? this.renderAddForm() : ''}

        ${this.renderFeedsList()}
      </div>
    `;
  }

  renderAddForm() {
    return `
      <div class="add-feed-form">
        <h4>Add RSS Feed</h4>
        <form id="addFeedForm">
          <div class="form-group">
            <label for="feedUrl">Feed URL</label>
            <input 
              type="url" 
              id="feedUrl" 
              name="url" 
              placeholder="https://example.com/feed.xml" 
              required
              class="form-control"
            />
            <small class="form-help">Enter the URL of an RSS or Atom feed</small>
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" id="notifyFriendsEmail" name="notify_friends_email" />
              Email friends when this feed updates
            </label>
            <small class="form-help">Friends who have new-post emails enabled will be notified</small>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary">Add Feed</button>
            <button type="button" class="btn btn-secondary cancel-btn">Cancel</button>
          </div>
        </form>
      </div>
    `;
  }

  renderFeedsList() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading feeds...</p>
        </div>
      `;
    }

    if (this.feeds.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-icon">📡</div>
          <p>No RSS feeds configured</p>
          <p class="empty-hint">Add a feed to automatically import posts</p>
        </div>
      `;
    }

    return `
      <div class="feeds-list">
        ${this.feeds.map(feed => this.renderFeedItem(feed)).join('')}
      </div>
    `;
  }

  renderFeedItem(feed) {
    const statusClass = feed.status === 'active' ? 'status-active' :
      feed.status === 'paused' ? 'status-paused' :
        feed.status === 'error' ? 'status-error' : 'status-pending';
    const statusIcon = feed.status === 'active' ? '✅' :
      feed.status === 'paused' ? '⏸️' :
        feed.status === 'error' ? '❌' : '⏳';

    const lastChecked = feed.last_checked_at
      ? new Date(feed.last_checked_at).toLocaleString()
      : 'Never';
    const nextCheck = feed.next_check_after
      ? new Date(feed.next_check_after).toLocaleString()
      : 'Soon';

    return `
      <div class="feed-item" data-feed-id="${feed.id}">
        <div class="feed-header">
          <div class="feed-info">
            <h4 class="feed-title">${escapeHtml(feed.title || feed.feed_url || 'Untitled Feed')}</h4>
            <div class="feed-meta">
              <span class="feed-url">${escapeHtml(feed.discovered_feed_url || feed.feed_url)}</span>
              ${feed.site_url ? `<span class="feed-site">${escapeHtml(feed.site_url)}</span>` : ''}
            </div>
          </div>
          <div class="feed-status ${statusClass}">
            <span class="status-icon">${statusIcon}</span>
            <span class="status-text">${feed.status}</span>
          </div>
        </div>
        
        <div class="feed-details">
          <div class="feed-stat">
            <span class="stat-label">Last checked:</span>
            <span class="stat-value">${lastChecked}</span>
          </div>
          <div class="feed-stat">
            <span class="stat-label">Next check:</span>
            <span class="stat-value">${nextCheck}</span>
          </div>
          ${feed.failure_count > 0 ? `
            <div class="feed-stat">
              <span class="stat-label">Failures:</span>
              <span class="stat-value error">${feed.failure_count}</span>
            </div>
          ` : ''}
        </div>

        <div class="feed-actions">
          <button type="button" class="btn btn-sm btn-outline email-toggle-btn"
                  data-feed-id="${feed.id}"
                  data-notify="${feed.notify_friends_email ? '1' : '0'}"
                  title="Friend email notifications">
            ${feed.notify_friends_email ? '🔔' : '🔕'}
          </button>
          ${feed.status === 'active' ? `
            <button class="btn btn-sm btn-secondary pause-btn" data-feed-id="${feed.id}">Pause</button>
          ` : feed.status === 'paused' ? `
            <button class="btn btn-sm btn-primary resume-btn" data-feed-id="${feed.id}">Resume</button>
          ` : ''}
          <button class="btn btn-sm btn-danger delete-btn" data-feed-id="${feed.id}">Delete</button>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Add feed button
    const addBtn = this.querySelector('.add-feed-btn');
    addBtn?.addEventListener('click', () => {
      this.showAddForm = true;
      this.render();
      this.attachEventListeners();
      this.querySelector('#feedUrl')?.focus();
    });

    // Cancel add form
    const cancelBtn = this.querySelector('.cancel-btn');
    cancelBtn?.addEventListener('click', () => {
      this.showAddForm = false;
      this.render();
      this.attachEventListeners();
    });

    // Add feed form submit
    const form = this.querySelector('#addFeedForm');
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleAddFeed(e.target);
    });

    // Pause/Resume/Delete/Email toggle buttons
    this.addEventListener('click', async (e) => {
      const emailBtn = e.target.closest('.email-toggle-btn');
      if (emailBtn) {
        const feedId = parseInt(emailBtn.dataset.feedId, 10);
        const currentlyOn = emailBtn.dataset.notify === '1';
        try {
          await window.API.rss.update(feedId, { notify_friends_email: !currentlyOn });
          await this.loadFeeds();
          this.showSuccess(currentlyOn ? 'Friend emails off' : 'Friend emails on');
        } catch (error) {
          window.logger.error('Failed to toggle RSS email:', error);
          this.showError('Failed to update email setting');
        }
        return;
      }

      const btn = e.target.closest('.pause-btn, .resume-btn, .delete-btn');
      if (!btn) return;

      const feedId = parseInt(btn.dataset.feedId, 10);
      if (btn.classList.contains('pause-btn')) {
        await this.handlePauseFeed(feedId);
      } else if (btn.classList.contains('resume-btn')) {
        await this.handleResumeFeed(feedId);
      } else if (btn.classList.contains('delete-btn')) {
        await this.handleDeleteFeed(feedId);
      }
    });
  }

  async loadFeeds() {
    this.isLoading = true;
    this.render();

    try {
      this.feeds = await window.API.rss.list();
    } catch (error) {
      window.logger.error('Failed to load RSS feeds:', error);
      this.showError('Failed to load feeds. Please try again.');
    } finally {
      this.isLoading = false;
      this.render();
      this.attachEventListeners();
    }
  }

  async handleAddFeed(form) {
    const formData = new FormData(form);
    const url = formData.get('url');
    const notifyFriendsEmail = formData.get('notify_friends_email') === 'on';

    if (!url || !url.trim()) {
      this.showError('Please enter a feed URL');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Adding...';

    try {
      await window.API.rss.add({ url: url.trim(), notify_friends_email: notifyFriendsEmail });
      this.showAddForm = false;
      await this.loadFeeds();
      this.showSuccess('Feed added successfully! It will be checked soon.');
    } catch (error) {
      window.logger.error('Failed to add feed:', error);
      const message = error.message || error.error || 'Failed to add feed';
      this.showError(message);
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
    }
  }

  async handlePauseFeed(feedId) {
    try {
      await window.API.rss.update(feedId, { status: 'paused' });
      await this.loadFeeds();
      this.showSuccess('Feed paused');
    } catch (error) {
      window.logger.error('Failed to pause feed:', error);
      this.showError('Failed to pause feed');
    }
  }

  async handleResumeFeed(feedId) {
    try {
      await window.API.rss.update(feedId, { status: 'active' });
      await this.loadFeeds();
      this.showSuccess('Feed resumed');
    } catch (error) {
      window.logger.error('Failed to resume feed:', error);
      this.showError('Failed to resume feed');
    }
  }

  async handleDeleteFeed(feedId) {
    if (!confirm('Are you sure you want to delete this feed? This will stop importing new posts.')) {
      return;
    }

    try {
      await window.API.rss.delete(feedId);
      await this.loadFeeds();
      this.showSuccess('Feed deleted');
    } catch (error) {
      window.logger.error('Failed to delete feed:', error);
      this.showError('Failed to delete feed');
    }
  }

  showError(message) {
    const alert = document.createElement('div');
    alert.className = 'alert alert-error';
    alert.textContent = message;
    this.appendChild(alert);
    setTimeout(() => alert.remove(), 5000);
  }

  showSuccess(message) {
    const alert = document.createElement('div');
    alert.className = 'alert alert-success';
    alert.textContent = message;
    this.appendChild(alert);
    setTimeout(() => alert.remove(), 3000);
  }

}

customElements.define('rss-feeds', RssFeeds);

